import React, { useState, useEffect } from 'react';
import { X, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { Geofence } from '../lib/supabase';

interface GeofenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (geofence: Partial<Geofence>) => Promise<void>;
  geofence?: Geofence | null;
  temporaryGeofence?: { center: [number, number]; radius: number } | null;
}

const GeofenceModal: React.FC<GeofenceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  geofence,
  temporaryGeofence,
}) => {
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [deviceFilter, setDeviceFilter] = useState<'all' | 'specific'>('all');
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (geofence) {
      setName(geofence.name);
      setActive(geofence.active);
      setDeviceFilter(geofence.devices && geofence.devices.length > 0 ? 'specific' : 'all');
      setSelectedDevices(geofence.devices || []);
    } else {
      setName('');
      setActive(true);
      setDeviceFilter('all');
      setSelectedDevices([]);
    }
    // Reset states when modal opens
    setError(null);
    setSuccess(false);
  }, [geofence, isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setError(null);
    
    if (!name.trim()) {
      setError('Please enter a name for the geofence');
      return;
    }

    setLoading(true);

    const geofenceData: Partial<Geofence> = {
      name: name.trim(),
      active,
      devices: deviceFilter === 'all' ? undefined : selectedDevices,
    };

    if (temporaryGeofence) {
      geofenceData.latitude = temporaryGeofence.center[0];
      geofenceData.longitude = temporaryGeofence.center[1];
      geofenceData.radius = temporaryGeofence.radius;
    }

    try {
      await onSave(geofenceData);
      setSuccess(true);
      
      // Show success message briefly then close
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 1500);
    } catch (err) {
      setError('Failed to save geofence. Please try again.');
      console.error('Error saving geofence:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="geofence-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="geofence-modal-content">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-traqpack-yellow rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-black" />
              </div>
              <h2 className="text-2xl font-bold">
                {geofence ? 'Edit' : 'Create'} Geofence
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors z-10"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Geofence Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Home, Office, Warehouse"
                className="w-full px-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-traqpack-yellow focus:border-traqpack-yellow"
                autoFocus
              />
            </div>

            {temporaryGeofence && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-medium text-sm mb-2">Geofence Details</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>Center: {temporaryGeofence.center[0].toFixed(6)}, {temporaryGeofence.center[1].toFixed(6)}</p>
                  <p>Radius: {temporaryGeofence.radius.toFixed(0)}m</p>
                  <p>Area: {(Math.PI * Math.pow(temporaryGeofence.radius, 2) / 1000000).toFixed(2)} km²</p>
                </div>
              </div>
            )}

            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="w-5 h-5 text-traqpack-yellow rounded focus:ring-traqpack-yellow"
                />
                <span className="text-sm font-medium">
                  Active (Generate alerts when devices enter/exit)
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Track Devices
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="all"
                    checked={deviceFilter === 'all'}
                    onChange={(e) => setDeviceFilter('all')}
                    className="text-traqpack-yellow focus:ring-traqpack-yellow"
                  />
                  <span className="text-sm">All devices</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="specific"
                    checked={deviceFilter === 'specific'}
                    onChange={(e) => setDeviceFilter('specific')}
                    className="text-traqpack-yellow focus:ring-traqpack-yellow"
                  />
                  <span className="text-sm">Specific devices (coming soon)</span>
                </label>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700">Geofence created successfully!</p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 border-2 border-black/20 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || success}
              className="px-6 py-3 bg-traqpack-yellow text-black rounded-xl font-bold hover:bg-traqpack-yellow/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  <span>Creating...</span>
                </>
              ) : success ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Created!</span>
                </>
              ) : (
                <span>{geofence ? 'Update' : 'Save'} Geofence</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeofenceModal;